import { useState, useEffect, useMemo, useCallback } from 'react';
import { Trash2, Plus, Pencil } from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import { useT } from '../i18n';
import type { InfrastructureWithProjects } from '../types';

export function InfrastructurePage() {
  const { t } = useT();
  const [items, setItems] = useState<InfrastructureWithProjects[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newItem, setNewItem] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: number } | null>(null);

  const loadData = useCallback(async () => {
    const data = await api.getInventoryInfrastructure() as InfrastructureWithProjects[];
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    await api.createInfrastructure(newItem.trim());
    setNewItem('');
    loadData();
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteInfrastructure(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      setDeleteTarget(null);
      alert(err.message);
    }
  };

  const startEdit = (item: InfrastructureWithProjects) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      await api.updateInfrastructure(editingId, editName.trim());
      cancelEdit();
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.name.toLowerCase().includes(q) ||
      i.projects.some(p => p.projectName.toLowerCase().includes(q))
    );
  }, [items, search]);

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('infra.title')}</h1>
          <p className="page-meta">{filtered.length === 1 ? t('infra.itemOne', { n: filtered.length }) : t('infra.itemMany', { n: filtered.length })}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="flex gap-8 flex-1">
          <input
            className="form-input max-w-sm"
            placeholder={t('infra.newItemPlaceholder')}
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button className="btn btn-accent btn-sm" onClick={handleAdd} disabled={!newItem.trim()}>
            <Plus size={14} /> {t('common.add')}
          </button>
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('infra.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('infra.emptyTitle')}</div>
          <div className="empty-state-desc">{search ? t('infra.emptyDescSearch') : t('infra.emptyDescDefault')}</div>
        </div>
      ) : (
        <table className="table table-sticky">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('infra.referencedBy')}</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(item => (
              <tr key={item.id}>
                <td>
                  {editingId === item.id ? (
                    <div className="flex gap-8 items-center">
                      <input
                        className="form-input flex-1"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                      <button className="btn btn-ghost btn-sm" onClick={saveEdit}>{t('common.save')}</button>
                      <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>{t('common.cancel')}</button>
                    </div>
                  ) : (
                    <span className="font-medium text-ink-1">{item.name}</span>
                  )}
                </td>
                <td>
                  {item.projects.length > 0 ? (
                    item.projects.map(p => (
                      <a
                        key={p.projectId}
                        href={`#project?id=${p.projectId}`}
                        className="badge badge-clickable"
                      >
                        {p.projectName}
                      </a>
                    ))
                  ) : (
                    <span className="text-ink-3 text-sm">{t('infra.noProjects')}</span>
                  )}
                </td>
                <td>
                  {editingId !== item.id && (
                    <>
                      <button className="btn-icon" title={t('common.edit')} onClick={() => startEdit(item)}>
                        <Pencil className="icon-sm" />
                      </button>
                      <button className="btn-icon" title={t('common.delete')} onClick={(e) => handleDelete(item.id, e)}>
                        <Trash2 className="icon-sm" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('infra.deleteTitle')}
        message={t('infra.deleteMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
